import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api, ApiError } from "../lib/api";
import type { Company, Region } from "../lib/types";

interface CompanyFormProps {
  initial?: Company;
  defaultRegionId?: string;
  onSaved: (company: Company) => void;
  onCancel: () => void;
}

export function CompanyForm({
  initial,
  defaultRegionId,
  onSaved,
  onCancel,
}: CompanyFormProps) {
  const isEdit = Boolean(initial);
  const qc = useQueryClient();

  const [regionId, setRegionId] = useState(
    initial?.regionId ?? defaultRegionId ?? "",
  );
  const [code, setCode] = useState(initial?.code ?? "");
  const [name, setName] = useState(initial?.name ?? "");
  const [registrationNumber, setRegistrationNumber] = useState(
    initial?.registrationNumber ?? "",
  );
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});
  const [formError, setFormError] = useState<string | null>(null);

  const regionsQ = useQuery({
    queryKey: ["regions"],
    queryFn: () => api.list<Region>("/v1/regions"),
  });

  const submitMut = useMutation({
    mutationFn: async () => {
      const payload: Record<string, unknown> = {
        code,
        name,
        registrationNumber: registrationNumber || undefined,
      };
      if (!isEdit) payload.regionId = regionId;
      if (isEdit) {
        const res = await api.patch<Company>(
          `/v1/companies/${initial!.id}`,
          payload,
        );
        return res.data;
      }
      const res = await api.post<Company>("/v1/companies", payload);
      return res.data;
    },
    onSuccess: (c) => {
      qc.invalidateQueries({ queryKey: ["companies"] });
      onSaved(c);
    },
    onError: (err) => {
      setFieldErrors({});
      setFormError(null);
      if (err instanceof ApiError) {
        const d = err.details as
          | { fieldErrors?: Record<string, string[]> }
          | undefined;
        if (d?.fieldErrors) setFieldErrors(d.fieldErrors);
        else setFormError(err.message);
      } else {
        setFormError((err as Error).message ?? "Unknown error");
      }
    },
  });

  const f = (name: string) => fieldErrors[name]?.[0];

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        submitMut.mutate();
      }}
      className="space-y-3 text-xs"
    >
      {formError && (
        <div className="rounded border border-border-danger bg-bg-danger p-2 text-text-danger">
          {formError}
        </div>
      )}
      <Row label="Region" error={f("regionId")}>
        <select
          className="input"
          value={regionId}
          onChange={(e) => setRegionId(e.target.value)}
          disabled={isEdit || regionsQ.isLoading}
        >
          <option value="">— select —</option>
          {(regionsQ.data?.data ?? []).map((r) => (
            <option key={r.id} value={r.id}>
              {r.code} · {r.name}
            </option>
          ))}
        </select>
      </Row>
      <Row label="Code" error={f("code")}>
        <input
          className="input"
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          maxLength={4}
          placeholder="e.g. C1"
        />
      </Row>
      <Row label="Name" error={f("name")}>
        <input
          className="input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={256}
        />
      </Row>
      <Row label="Registration No." error={f("registrationNumber")}>
        <input
          className="input"
          value={registrationNumber}
          onChange={(e) => setRegistrationNumber(e.target.value)}
          maxLength={64}
          placeholder="optional"
        />
      </Row>

      <div className="flex justify-end gap-2 pt-2">
        <button
          type="button"
          onClick={onCancel}
          className="rounded border border-border px-3 py-1 hover:bg-surface-1"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={submitMut.isPending}
          className="rounded bg-text-primary px-3 py-1 text-white hover:opacity-90 disabled:opacity-50"
        >
          {submitMut.isPending ? "Saving…" : isEdit ? "Save" : "Create"}
        </button>
      </div>
    </form>
  );
}

function Row({
  label,
  error,
  children,
}: {
  label: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <div className="mb-0.5 text-text-secondary">{label}</div>
      {children}
      {error && <div className="mt-0.5 text-[10px] text-red-600">{error}</div>}
    </label>
  );
}
